import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = `Reset password — ${BRAND.name}`; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] px-5">
      <div className="w-full max-w-md brand-card p-8">
        <Link to="/login" data-testid="reset-back-login" className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold">
          ← Sign in
        </Link>
        {done ? (
          <div className="mt-6 text-center" data-testid="reset-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-heading font-bold text-2xl mt-5">Password updated</h1>
            <p className="mt-2 text-sm text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-bold text-3xl mt-4 leading-tight">Set a new password</h1>
            <p className="mt-2 text-sm text-slate-600">Choose a strong password. Minimum 6 characters.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">New password</Label>
                <Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-input"
                  className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Confirm password</Label>
                <Input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-password-confirm-input"
                  className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11" />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="btn-emerald w-full text-sm">
                {loading ? "Updating…" : "Update password"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
